import { type Actor } from '@shared/board'
import { clsx } from 'clsx'
import { useSyncExternalStore } from 'react'
import { useI18n } from '@/i18n/i18n'
import { Avatar } from '@/components/Avatar'
import { useBoardCtx } from '../context'

/**
 * Who made something: "you", or the AI session by its title, each with its
 * avatar. `quiet`: the avatar alone, the name on hover (cards, where a name on
 * every card would be read before the card itself).
 */
export function Author({
  actor,
  className,
  quiet,
}: {
  actor: Actor
  className?: string
  quiet?: boolean
}) {
  const { t } = useI18n()
  const { me, sessions } = useBoardCtx()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const mine = actor.kind === 'user' && actor.id === me.id
  const name =
    actor.kind === 'session'
      ? (() => {
          const s = snap.sessions.find((x) => x.id === actor.id)
          return s?.title ? `AI · ${s.title}` : 'AI'
        })()
      : mine
        ? t('author.me')
        : t('author.someone')
  if (quiet)
    return (
      <span title={name} aria-label={name} className={clsx('inline-flex items-center', className)}>
        <Avatar actor={actor} />
      </span>
    )
  if (actor.kind === 'session') {
    return (
      <span
        className={clsx(
          'inline-flex max-w-full items-center gap-1.5 text-[11.5px] text-muted',
          className,
        )}
      >
        <Avatar actor={actor} />
        <span className="truncate">{name}</span>
      </span>
    )
  }
  return (
    <span className={clsx('inline-flex items-center gap-1.5 text-[11.5px] text-muted', className)}>
      <Avatar actor={actor} />
      {name}
    </span>
  )
}
